import { isEmpty } from '@fruitful/fruitils';
import {
    stripMoneyMapAccountFromRules,
    updateRuleAndGroupPriorities,
} from '@fruitful/money-map';
import {
    type DeepPartial,
    MoneyMapStatus,
    type MoneyMapUpsertDto,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { Redirect, router, Stack } from 'expo-router';
import { useAtomValue } from 'jotai';
import { capitalize } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
    FullScreenSpinner,
    LabelRegular,
    TooltipGroupProvider,
    TooltipTrigger,
    useToastController,
    XStack,
} from 'smoothie';

import FullScreenError from '../../common/components/FullScreenError';
import { Button } from '../../common/components/GroveButtons';
import { GroveLogo } from '../../common/components/GroveLogo';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import { TrackedEvent } from '../../logger/Events';
import { captureException, trackEvent } from '../../logger/Logger';
import MoneyMapEditor from '../../money-map/components/editor/MoneyMapEditor';
import { useDeleteMoneyMapRule } from '../hooks/useDeleteMoneyMapRule';
import { useDeleteMoneyMapRuleGroup } from '../hooks/useDeleteMoneyMapRuleGroup';
import { useGetMoneyMap } from '../hooks/useGetMoneyMap';
import {
    useFinalizeMoneyMap,
    usePublishMoneyMap,
} from '../hooks/useUpdateMoneyMapStatus';
import { useUpsertMoneyMap } from '../hooks/useUpsertMoneyMap';
import { MoneyMapPublishFinalizeEventSource } from '../moneyMapPublishFinalizeTracking';
import { useMoneyMapAccounts } from '../providers/MoneyMapAccountsProvider';
import {
    cleanAttributesForUpsert,
    getMoneyMapDefaultName,
    getMoneyMapValidationMessage,
    isMoneyMapEditable,
    isMoneyMapValidForPreviewOrActiveStatus,
} from '../utils/moneyMapUtils';
import MoneyMapStatusChangeConfirmModal from './MoneyMapStatusChangeConfirmModal';

interface MoneyMapHeaderTitleProps {
    moneyMapStatus: MoneyMapStatus;
    moneyMapTitle: string;
}

const MoneyMapHeaderTitle = ({
    moneyMapStatus,
    moneyMapTitle,
}: MoneyMapHeaderTitleProps) => {
    return (
        <XStack>
            <LabelRegular color="$gray800">
                {capitalize(moneyMapStatus)} /{' '}
            </LabelRegular>
            <LabelRegular>{moneyMapTitle}</LabelRegular>
        </XStack>
    );
};

interface MoneyMapHeaderButtonsProps {
    membershipId: string;
    moneyMapId?: string;
    showPublishButton: boolean;
    showFinalizeButton: boolean;
    onPressPublish: () => void;
    onPressFinalize: () => void;
    publishFinalizeEnabled: boolean;
    publishFinalizeDisabledMessage?: string | null;
    showSaveButton: boolean;
    onPressSave: () => void;
}

const MoneyMapHeaderButtons = ({
    membershipId,
    moneyMapId,
    showPublishButton,
    showFinalizeButton,
    onPressPublish,
    onPressFinalize,
    publishFinalizeEnabled,
    publishFinalizeDisabledMessage,
    showSaveButton,
    onPressSave,
}: MoneyMapHeaderButtonsProps) => {
    return (
        <XStack gap={12} marginHorizontal={16}>
            {showSaveButton && (
                <Button variant="secondary" size="tiny" onPress={onPressSave}>
                    Save
                </Button>
            )}
            {moneyMapId != null && (
                <Button
                    variant="secondary"
                    size="tiny"
                    target="_blank"
                    href={{
                        pathname:
                            '/money-map/[membershipId]/preview/[moneyMapId]',
                        params: { membershipId, moneyMapId },
                    }}
                >
                    Preview
                </Button>
            )}
            {showPublishButton &&
                (publishFinalizeDisabledMessage === '' ||
                publishFinalizeDisabledMessage === null ? (
                    <Button
                        variant="primary"
                        size="tiny"
                        onPress={onPressPublish}
                        disabled={!publishFinalizeEnabled}
                    >
                        Publish
                    </Button>
                ) : (
                    <TooltipTrigger
                        tooltipContent={publishFinalizeDisabledMessage ?? ''}
                        tooltipGroupName="money-map-header"
                    >
                        <Button
                            variant="primary"
                            size="tiny"
                            onPress={onPressPublish}
                            disabled={!publishFinalizeEnabled}
                        >
                            Publish
                        </Button>
                    </TooltipTrigger>
                ))}
            {showFinalizeButton &&
                (publishFinalizeDisabledMessage === '' ||
                publishFinalizeDisabledMessage === null ? (
                    <Button
                        variant="primary"
                        size="tiny"
                        onPress={onPressFinalize}
                        disabled={!publishFinalizeEnabled}
                    >
                        Finalize
                    </Button>
                ) : (
                    <TooltipTrigger
                        tooltipContent={publishFinalizeDisabledMessage ?? ''}
                        tooltipGroupName="money-map-header"
                    >
                        <Button
                            variant="primary"
                            size="tiny"
                            onPress={onPressFinalize}
                            disabled={!publishFinalizeEnabled}
                        >
                            Finalize
                        </Button>
                    </TooltipTrigger>
                ))}
        </XStack>
    );
};

interface MoneyMapPageWithHeaderProps {
    moneyMapId?: string;
}

const MoneyMapPageWithHeader = ({
    moneyMapId,
}: MoneyMapPageWithHeaderProps) => {
    const toast = useToastController();
    const { refetch: refetchMoneyMapAccounts } = useMoneyMapAccounts();

    const adminUser = useAtomValue(adminUserAtom);
    const canAccessMoneyMap =
        adminUser.authorization.selectedMembership.accounts.canEdit;
    const selectedMembership = useAtomValue(selectedMembershipAtom);

    const [moneyMap, setMoneyMap] = useState<
        DeepPartial<MoneyMapWithRulesAndAccounts>
    >({});
    const [isUpdating, setIsUpdating] = useState(false);
    const [showPublishButton, setShowPublishButton] = useState(false);
    const [showFinalizeButton, setShowFinalizeButton] = useState(false);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const {
        data: fetchedMoneyMap,
        isLoading: isLoadingMoneyMap,
        refetch: refetchMoneyMap,
    } = useGetMoneyMap(
        { id: moneyMapId as string },
        { enabled: moneyMapId != null },
    );

    const { mutateAsync: upsertMoneyMap } = useUpsertMoneyMap();
    const { mutateAsync: deleteMoneyMapRule } = useDeleteMoneyMapRule();
    const { mutateAsync: deleteMoneyMapRuleGroup } =
        useDeleteMoneyMapRuleGroup();

    useEffect(() => {
        if (fetchedMoneyMap != null) {
            setMoneyMap(fetchedMoneyMap);

            setShowPublishButton(
                fetchedMoneyMap.status === MoneyMapStatus.DRAFT,
            );

            setShowFinalizeButton(
                fetchedMoneyMap.status === MoneyMapStatus.PREVIEW,
            );
            setShowSaveButton(isMoneyMapEditable(fetchedMoneyMap));
        } else if (!isLoadingMoneyMap && selectedMembership != null) {
            if (isEmpty(moneyMap)) {
                // Set defaults
                setMoneyMap({
                    membershipId: selectedMembership?.id,
                    status: MoneyMapStatus.DRAFT,
                    title: getMoneyMapDefaultName(selectedMembership),
                });
            }

            setShowPublishButton(true);
            setShowFinalizeButton(false);
            setShowSaveButton(false);
        }
    }, [
        moneyMapId,
        JSON.stringify(fetchedMoneyMap),
        JSON.stringify(selectedMembership),
    ]);

    const publishFinalizeEnabled = useMemo(() => {
        return isMoneyMapValidForPreviewOrActiveStatus(moneyMap);
    }, [JSON.stringify(moneyMap)]);

    const publishFinalizeDisabledMessage = useMemo(() => {
        return getMoneyMapValidationMessage(moneyMap);
    }, [JSON.stringify(moneyMap)]);

    // Update changes to money map in local state
    const onUpdateMoneyMap = async (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ): Promise<boolean> => {
        // These fields are always required on the upsert dto, so provide as defaults and have the updates object override them for any updates.
        const {
            id,
            membershipId,
            title,
            status,
            monthlyIncomeCents,
            incomeMoneyMapAccountId,
        } = moneyMap;

        const updatedMoneyMap = {
            id,
            membershipId,
            title,
            status,
            monthlyIncomeCents,
            incomeMoneyMapAccountId,
            ...updates,
        };

        // Call save with updated data, so we don't have to wait for state to update
        await onPressSave(updatedMoneyMap);
        return true;
    };

    // Save money map to backend
    const onPressSave = async (
        updatedMoneyMap?: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => {
        if (
            moneyMap?.status !== MoneyMapStatus.DRAFT &&
            moneyMap?.status !== MoneyMapStatus.PREVIEW
        ) {
            return;
        }

        setIsUpdating(true);

        try {
            // TODO: Validate data
            const body = cleanAttributesForUpsert(
                updatedMoneyMap ?? moneyMap,
            ) as MoneyMapUpsertDto;

            // This API call takes care of cascade creating/upserting accounts and rules tied to the money map
            const upsertedMoneyMap = await upsertMoneyMap({
                body,
            });

            // If creating a new map, navigate to the route including the newly created id for further refetches to work
            if (moneyMapId == null && selectedMembership?.id != null) {
                router.replace({
                    pathname: '/money-map/[membershipId]',
                    params: {
                        membershipId: selectedMembership.id,
                        moneyMapId: upsertedMoneyMap.id,
                    },
                });
            }
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong!', { duration: 2000 });
        } finally {
            // Still refetch on failure to refresh local state to be accurate with backend
            await Promise.all([refetchMoneyMap(), refetchMoneyMapAccounts()]);
            setIsUpdating(false);
        }
    };

    const onRemoveRule = async (ruleId: string) => {
        setIsUpdating(true);
        try {
            const allRules = moneyMap.moneyMapRules ?? [];
            const deletedRule = allRules.find((rule) => rule.id === ruleId);
            const groupId = deletedRule?.moneyMapRuleGroupId;
            const rulesInSameGroup =
                groupId != null
                    ? allRules.filter((r) => r.moneyMapRuleGroupId === groupId)
                    : [];
            const shouldDeleteEmptyGroup =
                groupId != null && rulesInSameGroup.length === 1;

            await deleteMoneyMapRule({ urlParams: { id: ruleId } });

            if (shouldDeleteEmptyGroup) {
                await deleteMoneyMapRuleGroup({
                    urlParams: {
                        id: groupId,
                    },
                });

                const deletedGroup = (moneyMap.moneyMapRuleGroups ?? []).find(
                    (g) => g.id === groupId,
                );

                if (deletedGroup != null) {
                    const existingRules = stripMoneyMapAccountFromRules(
                        moneyMap.moneyMapRules ?? [],
                    );

                    const { rules: updatedRules, groups: updatedGroups } =
                        updateRuleAndGroupPriorities(
                            deletedGroup,
                            existingRules,
                            moneyMap.moneyMapRuleGroups ?? [],
                            'delete',
                        );

                    await onUpdateMoneyMap({
                        moneyMapRules: updatedRules,
                        moneyMapRuleGroups: updatedGroups,
                    });
                }
            } else if (deletedRule != null) {
                const existingRules = stripMoneyMapAccountFromRules(
                    moneyMap.moneyMapRules ?? [],
                );

                const { rules: updatedRules, groups: updatedGroups } =
                    updateRuleAndGroupPriorities(
                        deletedRule,
                        existingRules,
                        moneyMap.moneyMapRuleGroups ?? [],
                        'delete',
                    );

                await onUpdateMoneyMap({
                    moneyMapRules: updatedRules,
                    moneyMapRuleGroups: updatedGroups,
                });
            }

            await Promise.all([refetchMoneyMap(), refetchMoneyMapAccounts()]);
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong!', MutationErrorToastOptions());
        } finally {
            setIsUpdating(false);
        }
    };

    const onRemoveGroup = async (groupId: string) => {
        setIsUpdating(true);

        try {
            await deleteMoneyMapRuleGroup({
                urlParams: {
                    id: groupId,
                },
            });

            const deletedGroup = (moneyMap.moneyMapRuleGroups ?? []).find(
                (group) => group.id === groupId,
            );

            if (deletedGroup != null) {
                // Exclude existing moneyMapAccount dto object from upsert call.
                const existingRules = stripMoneyMapAccountFromRules(
                    moneyMap.moneyMapRules ?? [],
                );

                // Update priorities across all rules and groups
                const { rules: updatedRules, groups: updatedGroups } =
                    updateRuleAndGroupPriorities(
                        deletedGroup,
                        existingRules,
                        moneyMap.moneyMapRuleGroups ?? [],
                        'delete',
                    );

                await onUpdateMoneyMap({
                    moneyMapRules: updatedRules,
                    moneyMapRuleGroups: updatedGroups,
                });
            }

            await Promise.all([refetchMoneyMap(), refetchMoneyMapAccounts()]);
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong!', MutationErrorToastOptions());
        } finally {
            setIsUpdating(false);
        }
    };

    const { setMoneyMapToPreviewAsync } = usePublishMoneyMap();
    const { setMoneyMapToActiveAsync } = useFinalizeMoneyMap();

    const onPressPublish = async () => {
        if (moneyMapId == null) {
            return;
        }

        setIsUpdating(true);

        try {
            await setMoneyMapToPreviewAsync(moneyMapId);
            toast.show('Money map published!', MutationSuccessToastOptions);
            trackEvent(TrackedEvent.MoneyMapPublishedForPreview, {
                moneyMapId,
                membershipId: selectedMembership?.id ?? '',
                source: MoneyMapPublishFinalizeEventSource.EDITOR,
            });
        } catch (error) {
            captureException(error);
            toast.show(
                'Error in setting money map to preview',
                MutationErrorToastOptions(error),
            );
        } finally {
            await refetchMoneyMap();
            setIsUpdating(false);
        }
    };

    const onPressFinalize = async () => {
        if (moneyMapId == null) {
            return;
        }

        setIsUpdating(true);

        try {
            await setMoneyMapToActiveAsync(moneyMapId);
            toast.show('Money map finalized!', MutationSuccessToastOptions);
            trackEvent(TrackedEvent.MoneyMapFinalizedForAction, {
                moneyMapId,
                membershipId: selectedMembership?.id ?? '',
                source: MoneyMapPublishFinalizeEventSource.EDITOR,
            });
        } catch (error) {
            captureException(error);
            toast.show(
                'Error publishing money map',
                MutationErrorToastOptions(error),
            );
        } finally {
            await refetchMoneyMap();
            setIsUpdating(false);
        }
    };

    if (selectedMembership == null) {
        router.back();
        return <FullScreenSpinner />;
    }

    // Wait for money map to fetch, upsert, or to set defaults
    if (isLoadingMoneyMap || isUpdating || isEmpty(moneyMap)) {
        return <FullScreenSpinner />;
    }

    if (!canAccessMoneyMap) {
        return <Redirect href="/" />;
    }

    // Go to preview page if Money Map is no longer editable.
    if (!isMoneyMapEditable(moneyMap)) {
        if (moneyMap.id == null) {
            return <FullScreenError />;
        }

        return (
            <Redirect
                href={`/money-map/${selectedMembership.id}/preview/${moneyMap.id}`}
            />
        );
    }

    return (
        <>
            <Stack.Screen
                options={{
                    headerLeft: () => (
                        <XStack marginHorizontal={16}>
                            <GroveLogo />
                        </XStack>
                    ),
                    headerTitle: () => (
                        <MoneyMapHeaderTitle
                            moneyMapStatus={moneyMap?.status as MoneyMapStatus}
                            moneyMapTitle={moneyMap?.title as string}
                        />
                    ),
                    headerTitleAlign: 'center',
                    headerBackVisible: true, // TODO: This isn't working, should still show the back button
                    headerRight: () => (
                        <MoneyMapHeaderButtons
                            membershipId={selectedMembership.id}
                            moneyMapId={moneyMap?.id}
                            showPublishButton={showPublishButton}
                            showFinalizeButton={showFinalizeButton}
                            onPressPublish={() => {
                                setIsConfirmModalOpen(true);
                            }}
                            onPressFinalize={() => {
                                setIsConfirmModalOpen(true);
                            }}
                            publishFinalizeEnabled={publishFinalizeEnabled}
                            publishFinalizeDisabledMessage={
                                publishFinalizeDisabledMessage
                            }
                            showSaveButton={showSaveButton}
                            onPressSave={async () => {
                                await onPressSave();
                            }}
                        />
                    ),
                }}
            />
            <MoneyMapEditor
                moneyMap={moneyMap}
                onUpdateMoneyMap={onUpdateMoneyMap}
                onRemoveRule={onRemoveRule}
                onRemoveGroup={onRemoveGroup}
            />
            {moneyMap?.status != null && (
                <MoneyMapStatusChangeConfirmModal
                    isOpen={isConfirmModalOpen}
                    onClose={() => {
                        setIsConfirmModalOpen(false);
                    }}
                    status={moneyMap.status}
                    onPressConfirm={async () => {
                        if (moneyMap.status === MoneyMapStatus.DRAFT) {
                            await onPressPublish();
                        } else if (moneyMap.status === MoneyMapStatus.PREVIEW) {
                            await onPressFinalize();
                        }
                    }}
                />
            )}
            <TooltipGroupProvider tooltipGroupName="money-map-header" />
        </>
    );
};

export default MoneyMapPageWithHeader;
